import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { checkCandidatDuplicate } from '../../services/CandidatDuplicateService';

const MATCH_LABEL = {
  linkedin: 'Même profil LinkedIn',
  name_company: 'Même nom, même entreprise',
  name: 'Même nom',
};

/** Duplicate check of a person against the CRM, re-run when the identity fields change. */
export function useDuplicateCheck({ linkedin, prenom, nom, employeur }, enabled = true) {
  const [state, setState] = useState({ loading: false, matches: [], error: false });

  useEffect(() => {
    if (!enabled || (!linkedin && !(prenom && nom))) {
      setState({ loading: false, matches: [], error: false });
      return undefined;
    }
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(() => {
      checkCandidatDuplicate({ linkedin, prenom, nom, employeur })
        .then((matches) => alive && setState({ loading: false, matches, error: false }))
        .catch(() => alive && setState({ loading: false, matches: [], error: true }));
    }, 450);
    return () => { alive = false; clearTimeout(timer); };
  }, [linkedin, prenom, nom, employeur, enabled]);

  const sameProfile = state.matches.find((m) => m.match === 'linkedin') || null;
  return { ...state, sameProfile, hasSimilar: state.matches.length > 0 && !sameProfile };
}

const MatchRow = ({ m }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '8px', bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a' }}>
        {[m.prenom, m.nom].filter(Boolean).join(' ') || 'Sans nom'}
      </Typography>
      <Typography noWrap sx={{ fontSize: '0.74rem', color: '#64748b' }}>
        {[m.fonction, m.employeur].filter(Boolean).join(' · ') || '—'} · {MATCH_LABEL[m.match]}
      </Typography>
    </Box>
    <Link
      href={`/candidats/${m.id}/view`}
      target="_blank"
      rel="noopener"
      underline="hover"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.78rem', fontWeight: 700, color: '#03407b', flexShrink: 0 }}
    >
      Voir la fiche <OpenInNewIcon sx={{ fontSize: 14 }} />
    </Link>
  </Box>
);

/** Banner shown in the "Ajouter au CRM" forms. */
const DuplicateCandidates = ({ check }) => {
  const { loading, matches, error, sameProfile } = check;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748b', fontSize: '0.8rem' }}>
        <CircularProgress size={14} /> Recherche de doublons dans le CRM…
      </Box>
    );
  }
  if (error) {
    return <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>Vérification des doublons indisponible : vérifiez dans le CRM avant de créer.</Typography>;
  }
  if (!matches.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#16a34a', fontSize: '0.8rem', fontWeight: 600 }}>
        <CheckCircleOutlinedIcon sx={{ fontSize: 17 }} /> Aucun doublon trouvé dans le CRM.
      </Box>
    );
  }

  const tone = sameProfile
    ? { border: '#fecaca', bg: '#fef2f2', color: '#b91c1c', icon: <ErrorOutlinedIcon sx={{ fontSize: 18 }} /> }
    : { border: '#fde68a', bg: '#fffbeb', color: '#92400e', icon: <WarningAmberIcon sx={{ fontSize: 18 }} /> };

  return (
    <Box sx={{ p: 1.5, borderRadius: '12px', border: `1px solid ${tone.border}`, bgcolor: tone.bg }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: tone.color }}>
        {tone.icon}
        <Typography sx={{ fontSize: '0.86rem', fontWeight: 800 }}>
          {sameProfile ? 'Ce candidat existe déjà dans le CRM' : 'Doublon possible dans le CRM'}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.78rem', color: tone.color, mt: 0.25, mb: 1, lineHeight: 1.5 }}>
        {sameProfile
          ? 'Même profil LinkedIn : la fiche existante sera mise à jour, aucun doublon ne sera créé.'
          : 'Un candidat porte le même nom. Vérifiez sa fiche : si c’est la même personne, ne créez pas de nouveau candidat.'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {matches.map((m) => <MatchRow key={m.id} m={m} />)}
      </Box>
    </Box>
  );
};

export default DuplicateCandidates;
